import styled, { css } from 'styled-components'

import { breakpoints } from '@awazevr/design-system.utils.react'

export const ExplorationContainer = styled.section`
  /* Parent Stack adds a 12px flex gap below; 4px + 12px = 16px to the list. */
  margin-bottom: 0.25rem;
  background: color-mix(
    in srgb,
    ${({ theme }) => theme.ColorBrandPrimary} 15%,
    transparent
  );

  /* Mobile: full-bleed to the device edges (relies on the centred flex parent). */
  width: 100vw;
  padding: 1.25rem 0;

  @media only screen and (min-width: ${breakpoints[0]}px) {
    width: 100%;
    border-radius: 4px;
  }
`

export const ExplorationHeading = styled.h2`
  font-family: ${({ theme }) => theme.FontHeadingHeading200FontFamily};
  font-size: 20px;
  font-weight: ${({ theme }) => theme.FontHeadingHeading200FontWeight};
  line-height: ${({ theme }) => theme.FontHeadingHeading200LineHeight}px;
  color: ${({ theme }) => theme.ColorTextBodyOnLight};
  margin: 0 0 0.75rem 0;
  padding-left: ${({ theme }) => theme.GridBaseGridSmall0GutterSize}px;

  @media only screen and (min-width: ${breakpoints[0]}px) {
    font-size: ${({ theme }) => theme.FontHeadingHeading200FontSize}px;
    padding-left: 1.25rem;
  }
`

export const ExplorationTrack = styled.div<{ $ready: boolean }>`
  /* No overflow clip here on purpose — Swiper clips internally; an extra clip
     around the animating wrapper defeats GPU compositing and causes jank. */
  position: relative;
  opacity: ${({ $ready }) => ($ready ? 1 : 0)};
  transition: opacity 150ms ease;

  .swiper {
    /* Vertical room so Swiper's overflow:hidden doesn't clip the card drop
       shadow; the equal negative margin keeps the slides in place. */
    padding-top: 8px;
    padding-bottom: 8px;
    margin-top: -8px;
    margin-bottom: -8px;
  }

  .swiper-slide {
    width: 238px;
    height: auto;
  }
`

const explorationNavButton = css`
  position: absolute;
  top: 72px; /* 10px lower than true centre (62px for a 124px image) to align visually */
  z-index: 2;
  display: none;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.5);
  color: #ffffff;
  cursor: pointer;
  visibility: visible;
  transform: translateY(-50%);
  transition:
    opacity 0.25s ease,
    visibility 0.25s ease,
    transform 0.25s ease,
    background-color 0.15s ease-in-out;

  @media only screen and (min-width: ${breakpoints[1]}px) {
    display: flex;
  }

  &:hover,
  &:focus-visible {
    background-color: rgba(0, 0, 0, 0.7);
  }

  &:focus {
    outline: 1px solid #ffffff;
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &.swiper-button-lock {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
  }
`

export const ExplorationNavNext = styled.button`
  ${explorationNavButton}
  right: 12px;

  &.swiper-button-disabled {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
  }
`

export const ExplorationNavPrev = styled.button`
  ${explorationNavButton}
  left: 12px;

  &.swiper-button-disabled {
    opacity: 0;
    visibility: hidden;
    transform: translate(16px, -50%);
    pointer-events: none;
  }
`

export const ExplorationLink = styled.a`
  display: flex;
  flex-direction: column;
  width: 238px;
  flex-shrink: 0;
  text-decoration: none;
  border-radius: 4px;
  overflow: hidden;
  height: 100%;
  background: #ffffff;
  box-shadow:
    0 0.3px 0.5px 0 rgba(0, 0, 0, 0.1),
    0 2px 4px 0 rgba(0, 0, 0, 0.2);
  color: ${({ theme }) => theme.ColorTextBodyOnLight};
  user-select: none;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.ColorThemeLink};
    outline-offset: 2px;
  }
`

export const ExplorationImage = styled.img`
  width: 100%;
  height: 118px;
  object-fit: cover;
  display: block;
  user-select: none;
  -webkit-user-drag: none;

  @media only screen and (min-width: ${breakpoints[1]}px) {
    height: 124px;
  }
`

export const ExplorationLabel = styled.span`
  padding: 0.5rem 0.75rem;
  font-size: 14px;
  font-weight: 700;
`
